import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type RefObject,
} from 'react'
import noop from 'lib/function/noop'
import type { AssistantSurfaceId } from 'tripPlanner/assistant/api/types'

export type AssistantSurfaceLayoutKey = AssistantSurfaceId

const assistantSurfaceLayoutKeyBySurface: Record<AssistantSurfaceId, AssistantSurfaceLayoutKey> = {
  trip_planner: 'trip_planner',
  accommodation_offer_page: 'accommodation_offer_page',
}

const ASSISTANT_SURFACE_LAYOUT_KEYS = Object.values(assistantSurfaceLayoutKeyBySurface)

type AssistantSurfaceRailOffsets = Readonly<Partial<Record<AssistantSurfaceLayoutKey, number>>>
type AssistantSurfaceBoundaryElements = Readonly<Partial<Record<AssistantSurfaceLayoutKey, HTMLElement>>>

interface AssistantSurfaceLayoutContextValue {
  assistantRailOffsetPx: number
  assistantMainContentBoundaryRef: RefObject<HTMLElement | null>
  registerAssistantSurfaceRailOffset: (surfaceKey: AssistantSurfaceLayoutKey, offsetPx: number) => void
  registerAssistantSurfaceMainContentBoundary: (surfaceKey: AssistantSurfaceLayoutKey, element: HTMLElement | null) => void
}

const EMPTY_ASSISTANT_SURFACE_RAIL_OFFSETS: AssistantSurfaceRailOffsets = {}
const EMPTY_ASSISTANT_SURFACE_BOUNDARY_ELEMENTS: AssistantSurfaceBoundaryElements = {}
const EMPTY_ASSISTANT_MAIN_CONTENT_BOUNDARY_REF: RefObject<HTMLElement | null> = { current: null }

const defaultAssistantSurfaceLayoutContext: AssistantSurfaceLayoutContextValue = {
  assistantRailOffsetPx: 0,
  assistantMainContentBoundaryRef: EMPTY_ASSISTANT_MAIN_CONTENT_BOUNDARY_REF,
  registerAssistantSurfaceRailOffset: noop,
  registerAssistantSurfaceMainContentBoundary: noop,
}

const AssistantSurfaceLayoutContext = createContext<AssistantSurfaceLayoutContextValue>(defaultAssistantSurfaceLayoutContext)

export function getAssistantSurfaceLayoutKey(surface: AssistantSurfaceId): AssistantSurfaceLayoutKey {
  return assistantSurfaceLayoutKeyBySurface[surface]
}

function normalizeRailOffsetPx(offsetPx: number): number {
  return Number.isFinite(offsetPx) ? Math.max(0, offsetPx) : 0
}

function getMaxRailOffsetPx(railOffsets: AssistantSurfaceRailOffsets): number {
  return ASSISTANT_SURFACE_LAYOUT_KEYS.reduce((maxOffsetPx, surfaceKey) => (
    Math.max(maxOffsetPx, railOffsets[surfaceKey] ?? 0)
  ), 0)
}

function getActiveSurfaceKey(railOffsets: AssistantSurfaceRailOffsets): AssistantSurfaceLayoutKey | undefined {
  return ASSISTANT_SURFACE_LAYOUT_KEYS.reduce<AssistantSurfaceLayoutKey | undefined>((activeSurfaceKey, surfaceKey) => {
    const offsetPx = railOffsets[surfaceKey] ?? 0
    if (offsetPx <= 0) {
      return activeSurfaceKey
    }
    if (!activeSurfaceKey || offsetPx > (railOffsets[activeSurfaceKey] ?? 0)) {
      return surfaceKey
    }
    return activeSurfaceKey
  }, undefined)
}

function getActiveMainContentBoundaryElement(
  railOffsets: AssistantSurfaceRailOffsets,
  boundaryElements: AssistantSurfaceBoundaryElements,
): HTMLElement | null {
  const activeSurfaceKey = getActiveSurfaceKey(railOffsets)

  if (!activeSurfaceKey) {
    return null
  }

  return boundaryElements[activeSurfaceKey] ?? null
}

function updateRailOffset(
  railOffsets: AssistantSurfaceRailOffsets,
  surfaceKey: AssistantSurfaceLayoutKey,
  offsetPx: number,
): AssistantSurfaceRailOffsets {
  const normalizedOffsetPx = normalizeRailOffsetPx(offsetPx)

  if (normalizedOffsetPx === 0) {
    if (!(surfaceKey in railOffsets)) {
      return railOffsets
    }

    const nextRailOffsets = { ...railOffsets }
    delete nextRailOffsets[surfaceKey]
    return nextRailOffsets
  }

  if (railOffsets[surfaceKey] === normalizedOffsetPx) {
    return railOffsets
  }

  return {
    ...railOffsets,
    [surfaceKey]: normalizedOffsetPx,
  }
}

function updateBoundaryElement(
  boundaryElements: AssistantSurfaceBoundaryElements,
  surfaceKey: AssistantSurfaceLayoutKey,
  element: HTMLElement | null,
): AssistantSurfaceBoundaryElements {
  if (!element) {
    if (!(surfaceKey in boundaryElements)) {
      return boundaryElements
    }

    const nextBoundaryElements = { ...boundaryElements }
    delete nextBoundaryElements[surfaceKey]
    return nextBoundaryElements
  }

  if (boundaryElements[surfaceKey] === element) {
    return boundaryElements
  }

  return {
    ...boundaryElements,
    [surfaceKey]: element,
  }
}

export function AssistantSurfaceLayoutProvider({ children }: PropsWithChildren) {
  const [railOffsets, setRailOffsets] = useState<AssistantSurfaceRailOffsets>(EMPTY_ASSISTANT_SURFACE_RAIL_OFFSETS)
  const [boundaryElements, setBoundaryElements] = useState<AssistantSurfaceBoundaryElements>(EMPTY_ASSISTANT_SURFACE_BOUNDARY_ELEMENTS)
  const assistantMainContentBoundaryRef = useRef<HTMLElement | null>(null)
  const assistantRailOffsetPx = getMaxRailOffsetPx(railOffsets)
  assistantMainContentBoundaryRef.current = getActiveMainContentBoundaryElement(railOffsets, boundaryElements)
  const registerAssistantSurfaceRailOffset = useCallback((surfaceKey: AssistantSurfaceLayoutKey, offsetPx: number) => {
    setRailOffsets(currentRailOffsets => updateRailOffset(currentRailOffsets, surfaceKey, offsetPx))
  }, [])
  const registerAssistantSurfaceMainContentBoundary = useCallback((surfaceKey: AssistantSurfaceLayoutKey, element: HTMLElement | null) => {
    setBoundaryElements(currentBoundaryElements => updateBoundaryElement(currentBoundaryElements, surfaceKey, element))
  }, [])
  const contextValue = useMemo(() => ({
    assistantRailOffsetPx,
    assistantMainContentBoundaryRef,
    registerAssistantSurfaceRailOffset,
    registerAssistantSurfaceMainContentBoundary,
  }), [
    assistantMainContentBoundaryRef,
    assistantRailOffsetPx,
    registerAssistantSurfaceMainContentBoundary,
    registerAssistantSurfaceRailOffset,
  ])

  return (
    <AssistantSurfaceLayoutContext.Provider value={contextValue}>
      {children}
    </AssistantSurfaceLayoutContext.Provider>
  )
}

export function useAssistantSurfaceLayout() {
  return useContext(AssistantSurfaceLayoutContext)
}

export function useAssistantSurfaceMainContentBoundary<TElement extends HTMLElement>(
  surfaceKey: AssistantSurfaceLayoutKey,
  externalRef?: RefObject<TElement | null>,
) {
  const { registerAssistantSurfaceMainContentBoundary } = useAssistantSurfaceLayout()

  return useCallback((element: TElement | null) => {
    if (externalRef) {
      externalRef.current = element
    }

    registerAssistantSurfaceMainContentBoundary(surfaceKey, element)
  }, [externalRef, registerAssistantSurfaceMainContentBoundary, surfaceKey])
}

export function useAssistantSurfaceRailOffset(surfaceKey: AssistantSurfaceLayoutKey, offsetPx: number) {
  const { registerAssistantSurfaceRailOffset } = useAssistantSurfaceLayout()

  useEffect(() => {
    registerAssistantSurfaceRailOffset(surfaceKey, offsetPx)

    return () => registerAssistantSurfaceRailOffset(surfaceKey, 0)
  }, [offsetPx, registerAssistantSurfaceRailOffset, surfaceKey])
}

export function useAssistantSurfaceDropdownBoundary(fallbackBoundaryRef?: RefObject<HTMLElement | null>) {
  const {
    assistantMainContentBoundaryRef,
    assistantRailOffsetPx,
  } = useAssistantSurfaceLayout()
  const isAssistantBoundaryActive = assistantRailOffsetPx > 0

  return useMemo(() => ({
    autoHeight: isAssistantBoundaryActive ? true : undefined,
    boundaryRef: isAssistantBoundaryActive ? assistantMainContentBoundaryRef : fallbackBoundaryRef,
    portaled: isAssistantBoundaryActive ? true : undefined,
  }), [assistantMainContentBoundaryRef, fallbackBoundaryRef, isAssistantBoundaryActive])
}
